import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Ticket,
  Plus,
  Trash2,
  Copy,
  Percent,
  DollarSign,
  Gift,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2
} from 'lucide-react';

const AdminCupones = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 20,
    applies_to: 'all',
    max_uses: '',
    max_uses_per_user: 1,
    min_purchase_amount: 0,
    description: '',
    is_vip: false
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/coupons/admin/list');
      setCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los cupones",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const payload = {
        ...newCoupon,
        max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
        discount_value: parseFloat(newCoupon.discount_value),
        min_purchase_amount: parseFloat(newCoupon.min_purchase_amount) || 0
      };

      await api.post('/coupons/admin/create', payload);
      
      toast({
        title: "Cupón creado",
        description: `Cupón ${payload.code || 'auto-generado'} creado exitosamente`
      });
      
      setShowCreateModal(false);
      setNewCoupon({
        code: '',
        discount_type: 'percentage',
        discount_value: 20,
        applies_to: 'all',
        max_uses: '',
        max_uses_per_user: 1,
        min_purchase_amount: 0,
        description: '',
        is_vip: false
      });
      fetchCoupons();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo crear el cupón",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeactivate = async (code) => {
    try {
      await api.put(`/coupons/admin/${code}/deactivate`);
      toast({
        title: "Cupón desactivado",
        description: `Cupón ${code} ha sido desactivado`
      });
      fetchCoupons();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo desactivar el cupón",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`¿Eliminar cupón ${code}?`)) return;
    
    try {
      await api.delete(`/coupons/admin/${code}`);
      toast({
        title: "Cupón eliminado",
        description: `Cupón ${code} ha sido eliminado`
      });
      fetchCoupons();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el cupón",
        variant: "destructive"
      });
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Código copiado",
      description: code
    });
  };

  const getDiscountIcon = (type) => {
    switch (type) {
      case 'percentage': return <Percent className="h-4 w-4" />;
      case 'fixed': return <DollarSign className="h-4 w-4" />;
      case 'free_access': return <Gift className="h-4 w-4" />;
      default: return <Ticket className="h-4 w-4" />;
    }
  };

  const getDiscountText = (coupon) => {
    switch (coupon.discount_type) {
      case 'percentage': return `${coupon.discount_value}% de descuento`;
      case 'fixed': return `$${coupon.discount_value} MXN de descuento`;
      case 'free_access': return 'Acceso gratuito';
      default: return 'Descuento';
    }
  };

  const getAppliesText = (applies_to) => {
    switch (applies_to) {
      case 'all': return 'Todo';
      case 'webinar': return 'Webinars';
      case 'membership': return 'Membresías';
      default: return applies_to;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-coupons-title">
            Cupones de Descuento
          </h1>
          <p className="text-gray-500 mt-1">
            Administra cupones para webinars y membresías
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchCoupons}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800" data-testid="create-coupon-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Cupón
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Cupón</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCoupon} className="space-y-4 mt-4">
                <div>
                  <Label>Código (opcional)</Label>
                  <Input
                    placeholder="Ej: UPIM30 (se genera automáticamente si está vacío)"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                    data-testid="coupon-code-input"
                  />
                </div>

                <div>
                  <Label>Tipo de Descuento</Label>
                  <Select
                    value={newCoupon.discount_type}
                    onValueChange={(value) => setNewCoupon({...newCoupon, discount_type: value})}
                  >
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                      <SelectItem value="fixed">Monto Fijo ($)</SelectItem>
                      <SelectItem value="free_access">Acceso Gratuito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newCoupon.discount_type !== 'free_access' && (
                  <div>
                    <Label>
                      {newCoupon.discount_type === 'percentage' ? 'Porcentaje (0-100)' : 'Monto ($MXN)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max={newCoupon.discount_type === 'percentage' ? 100 : undefined}
                      value={newCoupon.discount_value}
                      onChange={(e) => setNewCoupon({...newCoupon, discount_value: e.target.value})}
                      required
                      data-testid="discount-value-input"
                    />
                  </div>
                )}

                <div>
                  <Label>Aplica a</Label>
                  <Select
                    value={newCoupon.applies_to}
                    onValueChange={(value) => setNewCoupon({...newCoupon, applies_to: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todo (Webinars y Membresías)</SelectItem>
                      <SelectItem value="webinar">Solo Webinars</SelectItem>
                      <SelectItem value="membership">Solo Membresías</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Usos máximos</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Ilimitado"
                      value={newCoupon.max_uses}
                      onChange={(e) => setNewCoupon({...newCoupon, max_uses: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Usos por usuario</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newCoupon.max_uses_per_user}
                      onChange={(e) => setNewCoupon({...newCoupon, max_uses_per_user: parseInt(e.target.value) || 1})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Compra mínima ($MXN)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newCoupon.min_purchase_amount}
                    onChange={(e) => setNewCoupon({...newCoupon, min_purchase_amount: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Descripción</Label>
                  <Input
                    placeholder="Ej: Descuento especial para miembros"
                    value={newCoupon.description}
                    onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={newCoupon.is_vip}
                    onCheckedChange={(checked) => setNewCoupon({...newCoupon, is_vip: checked})}
                  />
                  <Label>Cupón VIP (sin restricciones)</Label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gray-900 hover:bg-gray-800"
                  disabled={creating}
                  data-testid="submit-coupon-btn"
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Ticket className="h-4 w-4 mr-2" />
                  )}
                  Crear Cupón
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Ticket className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <p className="text-2xl font-bold">{coupons.length}</p>
                <p className="text-sm text-gray-500">Total Cupones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  {coupons.filter(c => c.is_active).length}
                </p>
                <p className="text-sm text-gray-500">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  {coupons.reduce((acc, c) => acc + (c.current_uses || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Usos Totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Gift className="h-8 w-8 text-amber-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">
                  {coupons.filter(c => c.is_vip).length}
                </p>
                <p className="text-sm text-gray-500">VIP</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coupons Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Cupones</CardTitle>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay cupones creados</p>
              <p className="text-sm mt-1">Crea tu primer cupón usando el botón de arriba</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Código</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Descuento</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Aplica a</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Usos</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm" data-testid={`coupon-${coupon.code}`}>
                            {coupon.code}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyCode(coupon.code)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                          {coupon.is_vip && (
                            <Badge className="bg-amber-100 text-amber-700">VIP</Badge>
                          )}
                        </div>
                        {coupon.description && (
                          <p className="text-xs text-gray-500 mt-1">{coupon.description}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getDiscountIcon(coupon.discount_type)}
                          <span className="font-medium">{getDiscountText(coupon)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">
                          {getAppliesText(coupon.applies_to)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600">
                          {coupon.current_uses || 0}
                          {coupon.max_uses && ` / ${coupon.max_uses}`}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {coupon.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Activo</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">Inactivo</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {coupon.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeactivate(coupon.code)}
                              className="text-yellow-600 hover:text-yellow-700"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(coupon.code)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCupones;
